import { useState } from 'react';
import { GraphSettings, CustomGridLine, CustomGridSet, CustomIntersectionPoint } from '@/types/simulation';
import { useTheme } from '@/contexts/ThemeContext';

interface Props {
  settings: GraphSettings;
  onChange: (updates: Partial<GraphSettings>) => void;
}

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
  '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#d946ef', '#f43f5e', '#000000', '#ffffff',
];

function GlassNumInput({ label, value, onChange, step = 1, isDark = true }: {
  label: string; value: number; onChange: (v: number) => void; step?: number; isDark?: boolean;
}) {
  const textMuted = isDark ? 'text-white/35' : 'text-gray-500';
  return (
    <div className="flex flex-col gap-1">
      <label className={`text-xs ${textMuted}`}>{label}</label>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="glass-input text-xs font-mono px-2.5 py-1.5 rounded-lg w-full"
      />
    </div>
  );
}

function ColorPicker({ value, onChange, isDark }: { value: string; onChange: (c: string) => void; isDark: boolean }) {
  return (
    <div className="flex flex-wrap gap-1">
      {PRESET_COLORS.map((c) => (
        <button
          key={c}
          onClick={() => onChange(c)}
          className={`w-5 h-5 rounded-full cursor-pointer transition-all hover:scale-110 ring-1 ${
            value === c ? 'ring-offset-1 ring-white scale-110' : 'ring-transparent'
          }`}
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
  );
}

function ToggleSwitch({
  label, value, onChange, isDark,
}: {
  label: string; value: boolean; onChange: () => void; isDark: boolean;
}) {
  const textMuted = isDark ? 'text-white/35' : 'text-gray-500';
  return (
    <div className="flex items-center justify-between">
      <span className={`text-xs ${textMuted}`}>{label}</span>
      <button
        onClick={onChange}
        className={`relative w-10 h-5 rounded-full transition-all duration-300 cursor-pointer ${
          value
            ? isDark
              ? 'bg-cyan-500/60 border border-cyan-400/50 shadow-[0_0_8px_rgba(0,217,255,0.4)]'
              : 'bg-sky-500 border border-sky-400'
            : 'glass border-opacity-10'
        }`}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full shadow transition-all duration-300 ${
          value ? 'left-5 bg-white' : `left-0.5 ${isDark ? 'bg-white' : 'bg-gray-400'}`
        }`} />
      </button>
    </div>
  );
}

export default function GraphSettingsSection({ settings, onChange }: Props) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const textMuted = isDark ? 'text-white/35' : 'text-gray-500';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const accentCyan = isDark ? 'text-cyan-400' : 'text-sky-600';

  const [activeTab, setActiveTab] = useState<'basic' | 'lines' | 'sets'>('basic');
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editingSetId, setEditingSetId] = useState<string | null>(null);
  const [editingIntersectionId, setEditingIntersectionId] = useState<string | null>(null);
  const [showIntersections, setShowIntersections] = useState(false);

  const customLines = settings.customLines ?? [];
  const customSets = settings.customSets ?? [];
  const customIntersections = settings.customIntersections ?? [];

  // showCustomIntersections フラグ（全体オン/オフ）
  const intersectionsEnabled = settings.showCustomIntersections !== false;

  const updateLines = (lines: CustomGridLine[]) => onChange({ customLines: lines });
  const updateSets = (sets: CustomGridSet[]) => onChange({ customSets: sets });
  const updateIntersections = (pts: CustomIntersectionPoint[]) => onChange({ customIntersections: pts });

  const addLine = () => {
    const newLine: CustomGridLine = {
      id: `line-${Date.now()}`,
      axis: 'x',
      position: 0,
      color: '#ef4444',
      width: 2,
      showLabel: true,
    };
    updateLines([...customLines, newLine]);
    setEditingLineId(newLine.id);
  };

  const addSet = () => {
    const newSet: CustomGridSet = {
      id: `set-${Date.now()}`,
      enabled: true,
      axis: 'x',
      start: -5,
      end: 5,
      step: 1,
      color: '#f59e0b',
      width: 1.5,
      showLabels: false,
      showDots: true,
      extendBeyond: false,
    };
    updateSets([...customSets, newSet]);
    setEditingSetId(newSet.id);
  };

  const addIntersection = () => {
    const newPt: CustomIntersectionPoint = {
      id: `pt-${Date.now()}`,
      x: 0,
      y: 0,
      color: '#ef4444',
      size: 4,
      visible: true,
      showLabel: true,
    };
    updateIntersections([...customIntersections, newPt]);
    setEditingIntersectionId(newPt.id);
  };

  const removeLine = (id: string) => {
    updateLines(customLines.filter((l) => l.id !== id));
    if (editingLineId === id) setEditingLineId(null);
  };

  const removeSet = (id: string) => {
    updateSets(customSets.filter((s) => s.id !== id));
    if (editingSetId === id) setEditingSetId(null);
  };

  const removeIntersection = (id: string) => {
    updateIntersections(customIntersections.filter((p) => p.id !== id));
    if (editingIntersectionId === id) setEditingIntersectionId(null);
  };

  const updateLine = (id: string, patch: Partial<CustomGridLine>) => {
    updateLines(customLines.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const updateSet = (id: string, patch: Partial<CustomGridSet>) => {
    updateSets(customSets.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const updateIntersection = (id: string, patch: Partial<CustomIntersectionPoint>) => {
    updateIntersections(customIntersections.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const editingLine = customLines.find((l) => l.id === editingLineId);
  const editingSet = customSets.find((s) => s.id === editingSetId);

  return (
    <div className="flex flex-col gap-3 animate-float-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <h2 className="section-label flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-artboard-line ${isDark ? 'text-orange-400' : 'text-orange-500'}`} />
          </div>
          グラフ表示
        </h2>
        <button
          onClick={() => onChange({
            xMin: -10, xMax: 10, yMin: -6, yMax: 6,
            gridStep: 1, gridVisible: true,
            customLines: [], customSets: [], customIntersections: [],
          })}
          className={`glass-btn ripple-btn text-xs ${textMuted} hover:${textPrimary} px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap flex items-center gap-1`}
        >
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-refresh-line text-xs" />
          </div>
          リセット
        </button>
      </div>

      {/* Tabs — 3 tabs only */}
      <div className="flex gap-1 p-1 rounded-full glass flex-shrink-0">
        {[
          { key: 'basic' as const, label: '基本', icon: 'ri-settings-3-line' },
          { key: 'lines' as const, label: '単一線', icon: 'ri-subtract-line' },
          { key: 'sets' as const, label: '規則セット', icon: 'ri-grid-line' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1 text-xs py-1.5 px-2 rounded-full cursor-pointer transition-all whitespace-nowrap ${
              activeTab === tab.key
                ? isDark ? 'bg-white/15 text-white' : 'bg-black/10 text-gray-900'
                : textMuted
            }`}
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className={`${tab.icon} text-xs`} />
            </div>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== BASIC TAB ===== */}
      {activeTab === 'basic' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <GlassNumInput label="X 最小" value={settings.xMin} onChange={(v) => onChange({ xMin: v })} isDark={isDark} />
            <GlassNumInput label="X 最大" value={settings.xMax} onChange={(v) => onChange({ xMax: v })} isDark={isDark} />
            <GlassNumInput label="Y 最小" value={settings.yMin} onChange={(v) => onChange({ yMin: v })} isDark={isDark} />
            <GlassNumInput label="Y 最大" value={settings.yMax} onChange={(v) => onChange({ yMax: v })} isDark={isDark} />
          </div>

          <div className="space-y-1.5">
            <div className="flex justify-between">
              <span className={`text-xs ${textMuted}`}>目盛り間隔</span>
              <span className={`text-xs font-mono ${accentCyan}`}>{settings.gridStep}</span>
            </div>
            <input
              type="range"
              min="0.25"
              max="5"
              step="0.25"
              value={settings.gridStep}
              onChange={(e) => onChange({ gridStep: Number(e.target.value) })}
              className={`w-full cursor-pointer ${isDark ? 'accent-cyan-400' : 'accent-sky-600'}`}
            />
          </div>

          <ToggleSwitch
            label="グリッド表示"
            value={settings.gridVisible}
            onChange={() => onChange({ gridVisible: !settings.gridVisible })}
            isDark={isDark}
          />

          {/* ---- 交点セクション ---- */}
          <div className={`border-t pt-3 ${isDark ? 'border-white/10' : 'border-black/10'}`}>
            {/* 交点ヘッダー：オン/オフ + 展開ボタン */}
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => setShowIntersections((v) => !v)}
                className={`flex items-center gap-1.5 text-xs font-medium cursor-pointer transition-colors ${
                  intersectionsEnabled ? (isDark ? 'text-white/70' : 'text-gray-700') : textMuted
                }`}
              >
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`ri-focus-3-line text-xs ${isDark ? 'text-orange-400' : 'text-orange-500'}`} />
                </div>
                交点
                <span className={`text-xs ${textMuted}`}>({customIntersections.length})</span>
                <div className="w-3 h-3 flex items-center justify-center">
                  {showIntersections ? <i className="ri-arrow-up-s-line text-xs" /> : <i className="ri-arrow-down-s-line text-xs" />}
                </div>
              </button>

              <div className="flex items-center gap-2">
                {/* 全体オン/オフトグル */}
                <button
                  onClick={() => onChange({ showCustomIntersections: !intersectionsEnabled })}
                  className={`relative w-9 h-4.5 rounded-full transition-all duration-300 cursor-pointer ${
                    intersectionsEnabled
                      ? isDark
                        ? 'bg-cyan-500/60 border border-cyan-400/50'
                        : 'bg-sky-500 border border-sky-400'
                      : 'glass border-opacity-10'
                  }`}
                  style={{ width: '2.25rem', height: '1.125rem' }}
                >
                  <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full shadow transition-all duration-300 ${
                    intersectionsEnabled ? 'left-[calc(100%-0.875rem-0.125rem)] bg-white' : `left-0.5 ${isDark ? 'bg-white/60' : 'bg-gray-400'}`
                  }`} />
                </button>
                {/* 追加ボタン */}
                <button
                  onClick={() => { addIntersection(); setShowIntersections(true); }}
                  className={`glass-btn ripple-btn text-xs ${accentCyan} px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap flex items-center gap-1`}
                >
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-add-line text-xs" />
                  </div>
                  追加
                </button>
              </div>
            </div>

            {/* 交点リスト（展開時のみ） */}
            {showIntersections && (
              <div className="space-y-1.5">
                {customIntersections.length === 0 && (
                  <div className={`text-xs ${textMuted} text-center py-3`}>交点がありません</div>
                )}
                {customIntersections.map((pt) => (
                  <div key={pt.id}>
                    <div
                      onClick={() => setEditingIntersectionId(editingIntersectionId === pt.id ? null : pt.id)}
                      className={`flex items-center justify-between px-2.5 py-2 rounded-lg cursor-pointer transition-all ${
                        editingIntersectionId === pt.id
                          ? isDark ? 'bg-white/10' : 'bg-black/5'
                          : 'hover:bg-white/5'
                      } ${pt.visible === false ? 'opacity-40' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full border"
                          style={{
                            backgroundColor: pt.visible === false ? 'transparent' : pt.color,
                            borderColor: pt.color,
                          }}
                        />
                        <span className={`text-xs font-mono ${textPrimary}`}>
                          ({pt.x}, {pt.y})
                        </span>
                        {pt.label && (
                          <span className={`text-xs ${textMuted}`}>{pt.label}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateIntersection(pt.id, { visible: pt.visible !== false ? false : true });
                          }}
                          className={`w-5 h-5 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
                            pt.visible === false ? textMuted : (isDark ? 'text-cyan-400' : 'text-sky-600')
                          }`}
                        >
                          <i className={`${pt.visible === false ? 'ri-eye-off-line' : 'ri-eye-line'} text-xs`} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeIntersection(pt.id); }}
                          className={`w-5 h-5 flex items-center justify-center rounded-full cursor-pointer hover:bg-red-500/20 ${textMuted}`}
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                      </div>
                    </div>

                    {/* インラインエディタ */}
                    {editingIntersectionId === pt.id && (
                      <div className={`mt-1 space-y-2.5 p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-black/3'}`}>
                        <div className="grid grid-cols-2 gap-2">
                          <GlassNumInput
                            label="X 座標"
                            value={pt.x}
                            onChange={(v) => updateIntersection(pt.id, { x: v })}
                            step={0.5}
                            isDark={isDark}
                          />
                          <GlassNumInput
                            label="Y 座標"
                            value={pt.y}
                            onChange={(v) => updateIntersection(pt.id, { y: v })}
                            step={0.5}
                            isDark={isDark}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <span className={`text-xs ${textMuted}`}>点の大きさ: {pt.size}px</span>
                          <input
                            type="range"
                            min="2"
                            max="10"
                            step="0.5"
                            value={pt.size}
                            onChange={(e) => updateIntersection(pt.id, { size: Number(e.target.value) })}
                            className={`w-full cursor-pointer ${isDark ? 'accent-cyan-400' : 'accent-sky-600'}`}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <span className={`text-xs ${textMuted}`}>色</span>
                          <ColorPicker
                            value={pt.color}
                            onChange={(c) => updateIntersection(pt.id, { color: c })}
                            isDark={isDark}
                          />
                        </div>

                        <ToggleSwitch
                          label="座標ラベルを表示"
                          value={pt.showLabel}
                          onChange={() => updateIntersection(pt.id, { showLabel: !pt.showLabel })}
                          isDark={isDark}
                        />

                        {pt.showLabel && (
                          <div className="flex flex-col gap-1">
                            <label className={`text-xs ${textMuted}`}>ラベル文字（空欄で自動）</label>
                            <input
                              type="text"
                              value={pt.label ?? ''}
                              onChange={(e) => updateIntersection(pt.id, { label: e.target.value || undefined })}
                              placeholder={`(${pt.x}, ${pt.y})`}
                              className="glass-input text-xs px-2.5 py-1.5 rounded-lg w-full"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== SINGLE LINES TAB ===== */}
      {activeTab === 'lines' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className={`text-xs ${textMuted}`}>単一補助線 ({customLines.length})</span>
            <button
              onClick={addLine}
              className={`glass-btn ripple-btn text-xs ${accentCyan} px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap flex items-center gap-1`}
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-add-line text-xs" />
              </div>
              追加
            </button>
          </div>

          <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
            {customLines.map((line) => (
              <div
                key={line.id}
                onClick={() => setEditingLineId(editingLineId === line.id ? null : line.id)}
                className={`flex items-center justify-between px-2.5 py-2 rounded-lg cursor-pointer transition-all ${
                  editingLineId === line.id
                    ? isDark ? 'bg-white/10' : 'bg-black/5'
                    : 'hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: line.color }} />
                  <span className={`text-xs ${textPrimary}`}>
                    {line.axis === 'x' ? 'X' : 'Y'} = {line.position}
                  </span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); removeLine(line.id); }}
                  className={`w-5 h-5 flex items-center justify-center rounded-full cursor-pointer hover:bg-red-500/20 ${textMuted}`}
                >
                  <i className="ri-close-line text-xs" />
                </button>
              </div>
            ))}
            {customLines.length === 0 && (
              <div className={`text-xs ${textMuted} text-center py-4`}>補助線がありません</div>
            )}
          </div>

          {editingLine && (
            <div className={`space-y-2.5 p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-black/3'}`}>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1">
                  <label className={`text-xs ${textMuted}`}>軸</label>
                  <div className="flex gap-1">
                    {(['x', 'y'] as const).map((a) => (
                      <button
                        key={a}
                        onClick={() => updateLine(editingLine.id, { axis: a })}
                        className={`flex-1 text-xs py-1.5 rounded-lg cursor-pointer transition-all ${
                          editingLine.axis === a
                            ? isDark ? 'bg-cyan-500/30 text-cyan-300' : 'bg-sky-500/20 text-sky-700'
                            : 'glass'
                        }`}
                      >
                        {a.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
                <GlassNumInput
                  label="位置"
                  value={editingLine.position}
                  onChange={(v) => updateLine(editingLine.id, { position: v })}
                  step={0.5}
                  isDark={isDark}
                />
              </div>

              <div className="space-y-1.5">
                <span className={`text-xs ${textMuted}`}>線の太さ: {editingLine.width}px</span>
                <input
                  type="range"
                  min="0.5"
                  max="6"
                  step="0.5"
                  value={editingLine.width}
                  onChange={(e) => updateLine(editingLine.id, { width: Number(e.target.value) })}
                  className={`w-full cursor-pointer ${isDark ? 'accent-cyan-400' : 'accent-sky-600'}`}
                />
              </div>

              <div className="space-y-1.5">
                <span className={`text-xs ${textMuted}`}>色</span>
                <ColorPicker
                  value={editingLine.color}
                  onChange={(c) => updateLine(editingLine.id, { color: c })}
                  isDark={isDark}
                />
              </div>

              <ToggleSwitch
                label="ラベル表示"
                value={editingLine.showLabel}
                onChange={() => updateLine(editingLine.id, { showLabel: !editingLine.showLabel })}
                isDark={isDark}
              />

              <div className="flex flex-col gap-1">
                <label className={`text-xs ${textMuted}`}>ラベル文字（空欄で自動）</label>
                <input
                  type="text"
                  value={editingLine.label ?? ''}
                  onChange={(e) => updateLine(editingLine.id, { label: e.target.value || undefined })}
                  placeholder={`${editingLine.axis} = ${editingLine.position}`}
                  className="glass-input text-xs px-2.5 py-1.5 rounded-lg w-full"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== GRID SETS TAB ===== */}
      {activeTab === 'sets' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className={`text-xs ${textMuted}`}>規則セット ({customSets.length})</span>
            <button
              onClick={addSet}
              className={`glass-btn ripple-btn text-xs ${accentCyan} px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap flex items-center gap-1`}
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-add-line text-xs" />
              </div>
              追加
            </button>
          </div>

          <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
            {customSets.map((set) => (
              <div
                key={set.id}
                onClick={() => setEditingSetId(editingSetId === set.id ? null : set.id)}
                className={`flex items-center justify-between px-2.5 py-2 rounded-lg cursor-pointer transition-all ${
                  editingSetId === set.id
                    ? isDark ? 'bg-white/10' : 'bg-black/5'
                    : 'hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: set.color }} />
                  <span className={`text-xs ${textPrimary}`}>
                    {set.axis.toUpperCase()}: {set.start} ~ {set.end} / 間隔 {set.step}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); updateSet(set.id, { enabled: !set.enabled }); }}
                    className={`w-5 h-5 flex items-center justify-center rounded-full cursor-pointer ${
                      set.enabled ? (isDark ? 'text-cyan-400' : 'text-sky-600') : textMuted
                    }`}
                  >
                    <i className={`${set.enabled ? 'ri-eye-line' : 'ri-eye-off-line'} text-xs`} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeSet(set.id); }}
                    className={`w-5 h-5 flex items-center justify-center rounded-full cursor-pointer hover:bg-red-500/20 ${textMuted}`}
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>
              </div>
            ))}
            {customSets.length === 0 && (
              <div className={`text-xs ${textMuted} text-center py-4`}>規則セットがありません</div>
            )}
          </div>

          {editingSet && (
            <div className={`space-y-2.5 p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-black/3'}`}>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1">
                  <label className={`text-xs ${textMuted}`}>軸</label>
                  <div className="flex gap-1">
                    {(['x', 'y'] as const).map((a) => (
                      <button
                        key={a}
                        onClick={() => updateSet(editingSet.id, { axis: a })}
                        className={`flex-1 text-xs py-1.5 rounded-lg cursor-pointer transition-all ${
                          editingSet.axis === a
                            ? isDark ? 'bg-cyan-500/30 text-cyan-300' : 'bg-sky-500/20 text-sky-700'
                            : 'glass'
                        }`}
                      >
                        {a.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
                <GlassNumInput
                  label="開始"
                  value={editingSet.start}
                  onChange={(v) => updateSet(editingSet.id, { start: v })}
                  step={1}
                  isDark={isDark}
                />
                <GlassNumInput
                  label="終了"
                  value={editingSet.end}
                  onChange={(v) => updateSet(editingSet.id, { end: v })}
                  step={1}
                  isDark={isDark}
                />
                <GlassNumInput
                  label="間隔"
                  value={editingSet.step}
                  onChange={(v) => updateSet(editingSet.id, { step: Math.max(0.1, v) })}
                  step={0.5}
                  isDark={isDark}
                />
              </div>

              <div className="space-y-1.5">
                <span className={`text-xs ${textMuted}`}>線の太さ: {editingSet.width}px</span>
                <input
                  type="range"
                  min="0.5"
                  max="4"
                  step="0.5"
                  value={editingSet.width}
                  onChange={(e) => updateSet(editingSet.id, { width: Number(e.target.value) })}
                  className={`w-full cursor-pointer ${isDark ? 'accent-cyan-400' : 'accent-sky-600'}`}
                />
              </div>

              <div className="space-y-1.5">
                <span className={`text-xs ${textMuted}`}>色</span>
                <ColorPicker
                  value={editingSet.color}
                  onChange={(c) => updateSet(editingSet.id, { color: c })}
                  isDark={isDark}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <ToggleSwitch
                  label="座標ラベル"
                  value={editingSet.showLabels}
                  onChange={() => updateSet(editingSet.id, { showLabels: !editingSet.showLabels })}
                  isDark={isDark}
                />
                <ToggleSwitch
                  label="交点に点"
                  value={editingSet.showDots}
                  onChange={() => updateSet(editingSet.id, { showDots: !editingSet.showDots })}
                  isDark={isDark}
                />
              </div>

              <ToggleSwitch
                label="交点より先に線を延長"
                value={editingSet.extendBeyond}
                onChange={() => updateSet(editingSet.id, { extendBeyond: !editingSet.extendBeyond })}
                isDark={isDark}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
